import type { Locale } from "@tutor/protocol";
import { createContext, useCallback, useContext, type ReactNode } from "react";

// Strings for components and widgets, English and French. Each component declares its
// own strings next to its code with `defineStrings`, so adding a widget never touches a
// shared dictionary. Item content and tutor text arrive already in the child's locale.

export type { Locale };

export type Vars = Readonly<Record<string, string | number>>;

export interface Strings<K extends string> {
  readonly en: Readonly<Record<K, string>>;
  readonly fr: Readonly<Record<K, string>>;
}

/** Declares a component's strings. French must have every English key, and no more. */
export function defineStrings<K extends string>(
  en: Readonly<Record<K, string>>,
  fr: Readonly<Record<K, string>>,
): Strings<K> {
  return { en, fr };
}

/** Fills `{name}` placeholders; numbers are formatted for the locale. */
export function format(locale: Locale, template: string, vars?: Vars): string {
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    if (value === undefined) return whole;
    return typeof value === "number" ? formatNumber(locale, value) : value;
  });
}

export function formatNumber(locale: Locale, value: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);
}

const LocaleContext = createContext<Locale>("en");

/** Sets the locale for every component and widget below it. */
export function UiProvider(props: { locale: Locale; children: ReactNode }) {
  return <LocaleContext.Provider value={props.locale}>{props.children}</LocaleContext.Provider>;
}

export function useUiLocale(): Locale {
  return useContext(LocaleContext);
}

/** A translator for one component's strings, in the current locale. */
export function useStrings<K extends string>(strings: Strings<K>): (key: K, vars?: Vars) => string {
  const locale = useUiLocale();
  return useCallback(
    (key: K, vars?: Vars) => format(locale, strings[locale][key], vars),
    [locale, strings],
  );
}
